import { getToken, onMessage, deleteToken } from 'firebase/messaging';
import { doc, setDoc, arrayUnion, arrayRemove, serverTimestamp } from 'firebase/firestore';
import { db, getMessagingIfSupported, VAPID_KEY } from './firebase';

// =====================================================================
// PUSH NOTIFICATIONS — client-side helpers
//
// Strategy:
//   - We store an array of fcmTokens per user (one per device they sign in on).
//   - Each token is added to the array when permission is granted.
//   - On sign-out, we remove that device's token so old devices don't get pushed.
//   - Tokens can rotate — we listen for refreshes and keep the array in sync.
// =====================================================================

/**
 * Ask the browser for notification permission, then fetch an FCM token
 * and save it to the user's Firestore doc.
 * 
 * Returns: { ok: bool, status: 'granted'|'denied'|'default'|'unsupported', token: string|null, error: any }
 * 
 * Caller can show toast/modal based on status.
 */
export async function requestNotificationPermission(userId) {
  if (!userId) return { ok: false, status: 'unsupported', token: null, error: 'No userId' };
  
  // Check if FCM is supported (Safari iOS < 16.4, some in-app browsers don't support)
  const messaging = await getMessagingIfSupported();
  if (!messaging) {
    return { ok: false, status: 'unsupported', token: null, error: 'FCM not supported on this browser' };
  }
  
  // Some browsers don't expose Notification at all
  if (typeof Notification === 'undefined') {
    return { ok: false, status: 'unsupported', token: null, error: 'Notification API not available' };
  }
  
  try {
    // Ask the browser. This returns 'granted', 'denied', or 'default' (dismissed).
    const permission = await Notification.requestPermission();
    
    if (permission !== 'granted') {
      return { ok: false, status: permission, token: null, error: null };
    }
    
    // Permission granted — fetch the FCM token. We use the existing Workbox SW
    // (which now imports firebase-messaging-sw.js code via vite-plugin-pwa's
    // workbox.importScripts setting). There's only ONE service worker at scope '/',
    // and it handles both PWA caching AND FCM background pushes.
    const swReg = await navigator.serviceWorker.ready;
    
    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: swReg,
    });
    
    if (!token) {
      return { ok: false, status: 'denied', token: null, error: 'No token returned — possibly blocked' };
    }
    
    // Save the token to Firestore
    await saveFcmTokenToUser(userId, token);
    
    return { ok: true, status: 'granted', token, error: null };
  } catch (e) {
    console.warn('FCM token fetch failed:', e);
    return { ok: false, status: 'denied', token: null, error: e };
  }
}

/**
 * Save an FCM token to a user's Firestore doc.
 * Uses arrayUnion so the same token isn't duplicated, but multiple devices
 * each get their own slot in the array.
 */
export async function saveFcmTokenToUser(userId, token) {
  if (!userId || !token) return;
  try {
    await setDoc(
      doc(db, 'users', userId),
      {
        fcmTokens: arrayUnion(token),
        fcmLastUpdated: serverTimestamp(),
      },
      { merge: true }
    );
  } catch (e) {
    console.warn('Failed to save FCM token:', e);
  }
}

/**
 * Remove an FCM token from a user's Firestore doc.
 * Call this on sign-out so we don't push to a device the user logged out of.
 */
export async function removeFcmTokenFromUser(userId, token) {
  if (!userId || !token) return;
  try {
    await setDoc(
      doc(db, 'users', userId),
      { fcmTokens: arrayRemove(token) },
      { merge: true }
    );
  } catch (e) {
    console.warn('Failed to remove FCM token:', e);
  }
}

/**
 * Delete the current FCM token (browser-side).
 * Useful on sign-out — we want this device to stop receiving pushes.
 */
export async function deleteCurrentFcmToken() {
  try {
    const messaging = await getMessagingIfSupported();
    if (!messaging) return;
    await deleteToken(messaging);
  } catch (e) {
    console.warn('Failed to delete FCM token:', e);
  }
}

/**
 * Subscribe to FOREGROUND notifications — fires when push arrives while app is open.
 * (Background notifications are handled by firebase-messaging-sw.js automatically.)
 *
 * Returns the unsubscribe function.
 */
export async function onForegroundMessage(callback) {
  const messaging = await getMessagingIfSupported();
  if (!messaging) return () => {};
  return onMessage(messaging, callback);
}